/**
 * StateFilterChips Component
 * Filter chips with counts for quick filtering by processing state
 */

import { getStateInfo, FILTER_STATES } from '../utils/processingState';

const StateFilterChips = ({
    stateCounts = {},
    selectedStates = [],
    onChange,
    showAllOption = true,
}) => {
    const totalCount = Object.values(stateCounts).reduce((sum, count) => sum + count, 0);

    const toggleState = (state) => {
        if (selectedStates.includes(state)) {
            onChange(selectedStates.filter(s => s !== state));
        } else {
            onChange([...selectedStates, state]);
        }
    };

    const clearFilter = () => {
        onChange([]);
    };

    return (
        <div className="flex flex-wrap gap-2">
            {/* All option */}
            {showAllOption && (
                <button
                    onClick={clearFilter}
                    className={`
 px-3 py-1.5 rounded-full text-sm font-medium transition-all
 flex items-center gap-2
 ${selectedStates.length === 0
 ? 'bg-primary text-white ring-2 ring-primary/30'
 : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
 }
 `}
                >
                    All
                    <span className={`
 px-1.5 py-0.5 rounded-full text-xs
 ${selectedStates.length === 0 ? 'bg-white/20' : 'bg-gray-600'}
 `}>
                        {totalCount}
                    </span>
                </button>
            )}

            {/* State chips */}
            {FILTER_STATES.map(state => {
                const info = getStateInfo(state);
                const count = stateCounts[state] || 0;
                const isSelected = selectedStates.includes(state);

                // Don't show states with zero count unless selected
                if (count === 0 && !isSelected) return null;

                return (
                    <button
                        key={state}
                        onClick={() => toggleState(state)}
                        className={`
 px-3 py-1.5 rounded-full text-sm font-medium transition-all
 flex items-center gap-2
 ${isSelected
 ? `${info.bgClass} ${info.textClass} ring-2 ${info.ringClass}/30`
                                : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                            }
                        `}
                    >
                        <info.Icon className="w-4 h-4" />
                        {info.label}
                        <span className={`
 px-1.5 py-0.5 rounded-full text-xs
 ${isSelected ? 'bg-white/20' : 'bg-gray-600'}
 `}>
                            {count}
                        </span>
                    </button>
                );
            })}

            {/* Quick filter presets */}
            <div className="flex items-center gap-1 ml-2 pl-2 border-l border-gray-700">
                <button
                    onClick={() => onChange(['DISCOVERED', 'CRAWLED', 'ENRICHED'])}
                    className={`
 px-2 py-1 text-xs transition-all
 ${['DISCOVERED', 'CRAWLED', 'ENRICHED'].every(s => selectedStates.includes(s))
 ? 'bg-blue-50 text-blue-700'
 : 'text-gray-400 hover:text-gray-300 hover:bg-gray-700'
 }
 `}
                    title="Show tenders ready for processing"
                >
                    Ready
                </button>
                <button
                    onClick={() => onChange(['CRAWLING', 'ENRICHING', 'PROCESSING', 'ASSESSING'])}
                    className={`
 px-2 py-1 text-xs transition-all
 ${['CRAWLING', 'ENRICHING', 'PROCESSING', 'ASSESSING'].some(s => selectedStates.includes(s))
 ? 'bg-yellow-50 text-yellow-700'
 : 'text-gray-400 hover:text-gray-300 hover:bg-gray-700'
 }
 `}
                    title="Show tenders currently being processed"
                >
                    In Progress
                </button>
                <button
                    onClick={() => onChange(['ERROR'])}
                    className={`
 px-2 py-1 text-xs transition-all
 ${selectedStates.includes('ERROR')
 ? 'bg-red-50 text-red-700'
 : 'text-gray-400 hover:text-gray-300 hover:bg-gray-700'
 }
 `}
                    title="Show tenders with errors"
                >
                    Errors{stateCounts.ERROR > 0 && ` (${stateCounts.ERROR})`}
                </button>
            </div>
        </div>
    );
};

export default StateFilterChips;
